import ReactMarkdown from 'react-markdown'
import styled, { css } from 'styled-components'

import { colors } from 'shared/lib'
import mediaQueries from 'shared/theme/mediaQueries'
import { Store } from 'shared/types/Store'
import { t } from 'localization'

const TP = 'shared.stores.components.FlightClubStore'

const FCNY_STORE_ID = 1
const FCLA_STORE_ID = 2
const FCMIA_STORE_ID = 3

const DATA_QA_STORE_MAP = {
  [FCNY_STORE_ID]: {
    address: 'FcnyStoreAddress',
    map: 'FcnyMapLink',
  },
  [FCLA_STORE_ID]: {
    address: 'FclaStoreAddress',
    map: 'FclaMapLink',
  },
  [FCMIA_STORE_ID]: {
    address: 'FcmiaStoreAddress',
    map: 'FcmiaMapLink',
  },
}

const NEW_LINE_RESPONSE = '\r\n'
const NEW_LINE_MARKDOWN = '  \r'

export const FlightClubStore = ({
  address1,
  address2,
  consignmentAddress1,
  consignmentAddress2,
  consignmentHours,
  customMessage,
  id,
  mapUrl,
  storeHours,
  title,
}: Store) => {
  return (
    <StorePanel>
      <MapLink
        data-qa={DATA_QA_STORE_MAP[id]?.map}
        data-testid="store-maps"
        rel="noopener noreferrer"
        target="_blank"
        href={mapUrl}
      >
        <img
          src="/static/map-group.png"
          alt={`${title} ${t(`${TP}.googleMapPin`, 'Google Map Pin')}`}
        />
      </MapLink>
      <StoreDetails>
        <StoreName>{title}</StoreName>
        <AddressLink target="_blank" href={mapUrl} data-qa={DATA_QA_STORE_MAP[id]?.address}>
          <StoreAddress>
            {address1}
            <br />
            {address2}
          </StoreAddress>
        </AddressLink>
        <StoreHoursTitle>{t(`${TP}.storeHours`, 'Store Hours')}</StoreHoursTitle>
        <StoreHours>
          <ReactMarkdown linkTarget="_blank">
            {storeHours.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
          </ReactMarkdown>
        </StoreHours>
        <StoreHoursTitle>{t(`${TP}.consignment`, 'Consignment')}</StoreHoursTitle>
        {consignmentAddress1 && (
          <StoreAddress>
            {consignmentAddress1}
            {consignmentAddress2 && (
              <>
                <br />
                {consignmentAddress2}
              </>
            )}
          </StoreAddress>
        )}
        <StoreHours>
          <ReactMarkdown linkTarget="_blank">
            {consignmentHours.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
          </ReactMarkdown>
        </StoreHours>
        {customMessage && (
          <div>
            <ReactMarkdown linkTarget="_blank">
              {customMessage.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
            </ReactMarkdown>
          </div>
        )}
      </StoreDetails>
    </StorePanel>
  )
}

const StorePanel = styled.div`
  padding: 20px;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  color: ${colors.FC2_GREY};
`

const StoreDetails = styled.div`
  overflow: auto;
  line-height: 1.4;
`

const StoreName = styled.h2`
  color: ${colors.FC2_BLACK};
  font-size: inherit;
  margin-top: 0;
  margin-bottom: 3px;
`

const MapLink = styled.a`
  display: block;
  float: left;
  margin-right: 10px;
  margin-bottom: 10px;
  ${mediaQueries.mobile} {
    float: none;
  }
`

const TextLink = css`
  cursor: pointer;
  color: ${colors.FC2_GREY};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const AddressLink = styled.a`
  ${TextLink}
`

const StoreHoursTitle = styled.h3`
  font-size: inherit;
  margin-top: 15px;
  margin-bottom: 0;
`

const StoreHours = styled.div`
  a {
    ${TextLink}
  }

  p {
    margin: 0;
  }
`

const StoreAddress = styled.address`
  font-style: normal;
`
